(ns hai.ui.components.collection.views.kanban
  "Kanban collection view: groups a collection's rows into declared, ordered
  lanes and renders them as a read-only board.

  Two parts:

  - `group-lanes` (HAI-001-01) - the pure grouping core, a function of the
    collection map. REPL-inspectable, no rendering.
  - `view` (HAI-001-02) - the board layout: an ordered column grid of lanes,
    each a header + count over a card list (or an empty-lane state), plus the
    collection's loading / error / no-items indicators. No pagination footer.

  Cards render through the collection's existing card-render hook
  (`:collection.view.card/render`), which expects a *form-wrapped* row (it reads
  the entity via `:form/current-value`). Unlike the editable table/cards views -
  which mint a real nested form per row via `form/collection` - this board is
  read-only, so it wraps each raw row in a lightweight `{:form/current-value row}`
  map rather than a live re-frame form. That is deliberate and kanban-native: a
  board of a few hundred cards has no edit state to carry, so paying for a form
  per card (and its setup effects) would be waste."
  (:require
   [hai.ui.components :as c]
   [hai.ui.components.collection.views.cards :as cards]
   [lambdaisland.ornament :as o]))

(def KanbanViewConfig
  "Dev-time config a collection declares to opt into this kanban view.

  Lives here, with the view that consumes it, but is *merged into the table
  schema* (`table/StaticTable` + `table/EditableTable`) so that
  `strip-extra-keys` keeps these keys through `decode-table-props` - otherwise
  they would be dropped as unknown extras before `group-lanes` (and the
  `settings/collection-view-options` view gating) ever saw them. All optional: a
  collection without kanban config simply omits them and keeps the default
  Table/Cards views."
  [:map
   [:collection.view.kanban/lanes
    {:optional true}
    :any]
   [:collection.view.kanban/bucket-fn
    {:optional true}
    :any]
   [:collection.view.kanban/group-by
    {:optional true}
    :any]])

(def view-option
  "The view-switcher descriptor for the kanban view, in the same
  `{:value :title}` shape as `settings/view-options`. A kanban board's consumer
  puts this in its `:view-options` so the generic settings machinery offers
  Kanban without settings needing any kanban-specific knowledge."
  {:value :collection.view/kanban
   :title "Kanban"})

(defn- lane-assign-fn
  "The row -> lane-id function for a collection.

  Uses `:collection.view.kanban/bucket-fn` when present (the general mapping,
  e.g. the tasks board's `task-bucket`); otherwise falls back to value-equality
  on the `:collection.view.kanban/group-by` attr. Rows are raw entities at this
  seam (the collection field's `:form.field/current-value` holds unwrapped
  maps), so both paths read entity attrs directly."
  [{:collection.view.kanban/keys [bucket-fn] group-attr :collection.view.kanban/group-by}]
  (or bucket-fn #(get % group-attr)))

(defn- catch-all-lane-id
  "Id of the lane flagged `:collection.view.kanban.lane/catch-all?`, if any.

  Optional: when a lane is so flagged, rows whose assigned lane-id matches no
  declared lane (including nil) collapse into it. With no flagged lane, such
  rows are dropped rather than dumped into an arbitrary lane."
  [lanes]
  (some (fn [{:collection.view.kanban.lane/keys [id catch-all?]}]
          (when catch-all? id))
        lanes))

(defn group-lanes
  "Group a collection's rows into ordered lanes.

  Reads rows from the collection form field
  (`:table/form` -> `:form/fields` -> `:table/rows` -> `:form.field/current-value`)
  and the `:collection.view.kanban/*` config off the collection. Pure: a
  function of the collection map, with no rendering.

  Returns a vector of
  `{:collection.view.kanban.lane/id :collection.view.kanban.lane/label
    :collection.view.kanban.lane/cards}` in declared lane order. Every declared
  lane is present, including empty ones (`:collection.view.kanban.lane/cards []`).
  Lane assignment is `bucket-fn` when supplied, else value-equality on the
  `group-by` attr. Rows whose assigned id matches no declared lane (including
  nil) collapse into the `:collection.view.kanban.lane/catch-all?` lane when one
  is declared, and are otherwise omitted."
  [{:collection.view.kanban/keys [lanes] :as collection}]
  (let [rows      (-> collection :table/form :form/fields :table/rows
                      :form.field/current-value)
        assign    (lane-assign-fn collection)
        lane-ids  (into #{} (map :collection.view.kanban.lane/id) lanes)
        catch-all (catch-all-lane-id lanes)
        by-lane   (group-by (fn [row]
                              (let [id (assign row)]
                                (cond
                                  (contains? lane-ids id) id
                                  catch-all               catch-all
                                  :else                   ::unmatched)))
                            rows)]
    (mapv (fn [{:collection.view.kanban.lane/keys [id label]}]
            {:collection.view.kanban.lane/id    id
             :collection.view.kanban.lane/label label
             :collection.view.kanban.lane/cards (vec (get by-lane id []))})
          lanes)))

;; ---------------------------------------------------------------------------
;; Board layout (HAI-001-02)
;; ---------------------------------------------------------------------------

(o/defstyled board :div
  "The lane grid. The column count follows the number of declared lanes via the
  `--kanban-lanes` custom property (set inline by `view`) rather than an inline
  `grid-template-columns`, so the narrow-viewport collapse to a single column
  still wins (an inline `grid-template-columns` would out-specify the media
  query)."
  {:display               "grid"
   :grid-template-columns "repeat(var(--kanban-lanes, 3), minmax(0, 1fr))"
   :gap                   "20px"
   :align-items           "start"}
  [:at-media {:max-width "768px"}
   {:grid-template-columns "1fr"}])

(o/defstyled lane :section
  :flex :flex-col
  {:gap "12px"})

(o/defstyled lane-header :div
  :flex :flex-row :items-center
  {:gap "8px"})

(o/defstyled lane-label :span
  {:font-weight "600"
   :color       c/--gray-12})

(o/defstyled lane-count :span
  :inline-flex :items-center :justify-center
  {:min-width     "20px"
   :padding       "0 6px"
   :border-radius "10px"
   :font-size     "12px"
   :background    c/--gray-3
   :color         c/--gray-11})

(o/defstyled lane-cards :div
  :flex :flex-col
  {:gap "8px"})

(o/defstyled lane-empty :div
  :flex :justify-center
  {:padding   "12px"
   :color     c/--gray-9
   :font-size "14px"})

(defn- render-card
  "Render one raw lane row through the collection's card-render hook.

  `cards/card-render` dispatches to `:collection.view.card/render` when present
  (the kanban case) and expects a form-wrapped row, so wrap the raw entity in a
  lightweight `{:form/current-value row}` map - see the ns docstring for why a
  full form is not minted here."
  [collection row]
  [cards/card-render collection {:form/current-value row}])

(defn- lane-column
  "One board column: a header (label + card count) over the lane's cards, or an
  empty-lane state when the lane holds no cards. Empty lanes still render - the
  declared lane set is the fixed column layout."
  [collection {:collection.view.kanban.lane/keys [label cards]}]
  [lane
   [lane-header
    [lane-label label]
    [lane-count (count cards)]]
   (if (seq cards)
     (into [lane-cards]
           (map-indexed
            (fn [i row]
              ^{:key (hash [i row])}
              [render-card collection row]))
           cards)
     [lane-empty "Nothing here."])])

(defn view
  "Render `collection` as a read-only kanban board.

  Groups rows into declared, ordered lanes (`group-lanes`) and lays them out as
  a column grid, each column a `lane-column`. Reuses the cards view's error
  indicator, but renders *no* loading indicator: a board's load transition is
  carried by its lanes filling in, and the bespoke tasks board showed no
  board-level spinner - a whole-board spinner over the lanes on every resolve
  would be a parity regression (surfaced in the [HAI-001-07] review; mirrors
  [HAI-001-06]'s per-lane rationale for dropping the no-items banner). An empty
  board reads as per-lane `Nothing here.` cells alone - it renders no
  collection-level no-items banner, which would double the per-lane empties (a
  board's empty state is per-lane by design; see [HAI-001-06]). Renders no
  pagination footer (a board shows its whole page) and no search bar / controls -
  those are supplied by the collection wrapper's controls, as for the table and
  cards views."
  [collection]
  (let [lanes (group-lanes collection)]
    [:<>
     (into [board {:style {"--kanban-lanes" (count lanes)}}]
           (map (fn [lane-def]
                  ^{:key (:collection.view.kanban.lane/id lane-def)}
                  [lane-column collection lane-def]))
           lanes)
     [cards/error-indicator collection]]))

(comment
  ;; ---------------------------------------------------------------------------
  ;; Inline testing for `group-lanes` (REPL-evaluable; verification is HITL, so
  ;; there is no automated test namespace - see the design doc's * Verification).
  ;; Eval a form, read the => expected result beneath it.
  ;; ---------------------------------------------------------------------------

  ;; Shape a collection the way `group-lanes` reads it: rows live on the
  ;; :table/rows form field's :form.field/current-value (raw entities), plus the
  ;; :collection.view.kanban/* config.
  (defn mk-collection [rows config]
    (merge {:table/form {:form/fields {:table/rows {:form.field/current-value rows}}}}
           config))

  ;; Compact view helper: [[lane-label [card-ids...]] ...]
  (defn labels+ids [collection]
    (mapv (fn [l] [(:collection.view.kanban.lane/label l)
                   (mapv :id (:collection.view.kanban.lane/cards l))])
          (group-lanes collection)))

  ;; --- Value-equality default (no bucket-fn), group-by :status ---
  (labels+ids
   (mk-collection
    [{:id 1 :status :todo} {:id 2 :status :doing}
     {:id 3 :status :todo} {:id 4 :status :done}]
    {:collection.view.kanban/group-by :status
     :collection.view.kanban/lanes
     [{:collection.view.kanban.lane/id :todo  :collection.view.kanban.lane/label "To Do"}
      {:collection.view.kanban.lane/id :doing :collection.view.kanban.lane/label "Doing"}
      {:collection.view.kanban.lane/id :done  :collection.view.kanban.lane/label "Done"}
      {:collection.view.kanban.lane/id :empty :collection.view.kanban.lane/label "Empty Lane"}]}))
  ;; => [["To Do" [1 3]] ["Doing" [2]] ["Done" [4]] ["Empty Lane" []]]
  ;; groups by attr value; empty lane present; declared order preserved.

  ;; --- bucket-fn path (mirrors hai.tasks.data/task-bucket) + catch-all lane ---
  (let [task-bucket (fn [task]
                      (case (:task/urgency task)
                        :task.urgency/now  :now
                        :task.urgency/next :next
                        :later))]
    (labels+ids
     (mk-collection
      [{:id 1 :task/urgency :task.urgency/now}
       {:id 2 :task/urgency :task.urgency/next}
       {:id 3 :task/urgency :task.urgency/now}
       {:id 4 :task/urgency nil}                   ; untriaged  -> Later
       {:id 5 :task/urgency :task.urgency/someday} ; unknown    -> Later
       {:id 6}]                                    ; missing    -> Later
      {:collection.view.kanban/bucket-fn task-bucket
       :collection.view.kanban/lanes
       [{:collection.view.kanban.lane/id :now   :collection.view.kanban.lane/label "Now"}
        {:collection.view.kanban.lane/id :next  :collection.view.kanban.lane/label "Next"}
        {:collection.view.kanban.lane/id :later :collection.view.kanban.lane/label "Later"
         :collection.view.kanban.lane/catch-all? true}]})))
  ;; => [["Now" [1 3]] ["Next" [2]] ["Later" [4 5 6]]]
  ;; nil / unknown / missing urgency all collapse into the catch-all lane.

  ;; --- No catch-all declared: unmatched / nil rows are dropped, not dumped ---
  (labels+ids
   (mk-collection
    [{:id 1 :status :todo}
     {:id 2 :status :archived}  ; matches no lane -> dropped
     {:id 3 :status nil}        ; nil             -> dropped
     {:id 4 :status :done}]
    {:collection.view.kanban/group-by :status
     :collection.view.kanban/lanes
     [{:collection.view.kanban.lane/id :todo :collection.view.kanban.lane/label "To Do"}
      {:collection.view.kanban.lane/id :done :collection.view.kanban.lane/label "Done"}]}))
  ;; => [["To Do" [1]] ["Done" [4]]]   ; :archived and nil dropped

  ;; --- Empty collection: every declared lane present, all empty ---
  (labels+ids
   (mk-collection
    []
    {:collection.view.kanban/group-by :status
     :collection.view.kanban/lanes
     [{:collection.view.kanban.lane/id :todo :collection.view.kanban.lane/label "To Do"}
      {:collection.view.kanban.lane/id :done :collection.view.kanban.lane/label "Done"}]}))
  ;; => [["To Do" []] ["Done" []]]
  )
